// src/pages/Accounting/Reconcile/Summary/index.tsx

import type React from "react";
import { useNavigate } from "react-router-dom";
import { Printer, Share2 } from "lucide-react";
import { PALETTE } from "../constants";
import { SummaryTable } from "./SummaryTable";
import { mockSummaryRows } from "./summaryMockData";

const COMPANY_NAME = "BrightCare Home Healthcare Services Inc.";

const TOP_ACTIONS: { label: string; to: string; primary?: boolean }[] = [
  { label: "Chart of accounts", to: "/accounting/chart-of-accounts" },
  { label: "Register", to: "/accounting/register" },
  { label: "Reconcile", to: "/accounting/reconcile", primary: true },
  { label: "History by account", to: "/accounting/reconcile/history" },
];

export default function ReconcileSummaryPage() {
  const navigate = useNavigate();

  const handlePrint = () => window.print();
  const handleShare = () => {
    // TODO: open share dialog (email + copy link) when backend is in place
    console.log("Share summary");
  };

  const generatedAt = new Date().toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div style={pageStyle}>
      {/* Breadcrumb + title + top-right actions */}
      <div style={headerStyles.wrap}>
        <div style={headerStyles.crumb}>
          <span style={headerStyles.crumbLink} onClick={() => navigate("/accounting")}>
            Accounting
          </span>
          <span style={headerStyles.crumbSep}>/</span>
          <span style={headerStyles.crumbLink} onClick={() => navigate("/accounting/reconcile")}>
            Reconcile
          </span>
          <span style={headerStyles.crumbSep}>/</span>
          <span style={{ color: PALETTE.ink, fontWeight: 600 }}>Summary</span>
        </div>
        <div style={headerStyles.titleRow}>
          <div style={{ minWidth: 0 }}>
            <h1 style={headerStyles.title}>Reconcile</h1>
          </div>
          <div style={headerStyles.actions}>
            {TOP_ACTIONS.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => navigate(a.to)}
                style={a.primary ? headerStyles.actionBtnPrimary : headerStyles.actionBtn}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={bodyStyle}>
        {/* Centered company header */}
        <div style={companyHeaderStyle}>
          <h2 style={companyNameStyle}>{COMPANY_NAME}</h2>
          <div style={subHeaderStyle}>Reconciliation Summary</div>
        </div>

        {/* Print and Share actions above the table */}
        <div style={toolbarStyle}>
          <button type="button" onClick={handlePrint} style={toolBtnStyle}>
            <Printer size={14} />
            Print
          </button>
          <button type="button" onClick={handleShare} style={toolBtnStyle}>
            <Share2 size={14} />
            Share
          </button>
        </div>

        <SummaryTable rows={mockSummaryRows} />

        <div style={footMetaStyle}>
          Generated {generatedAt} &middot; {COMPANY_NAME}
        </div>
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  width: "100%",
  margin: 0,
  padding: "28px 0 90px",
  background: PALETTE.bg,
  minHeight: "100vh",
};

const headerStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: "0 32px 14px" },
  crumb: {
    fontSize: 13,
    color: PALETTE.ink3,
    marginBottom: 4,
    fontWeight: 500,
  },
  crumbLink: { color: PALETTE.ink3, cursor: "pointer" },
  crumbSep: { color: PALETTE.ink3, margin: "0 6px" },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: PALETTE.ink,
  },
  actions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },
  actionBtn: {
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    color: PALETTE.ink,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  actionBtnPrimary: {
    background: "#0E1A1A",
    color: "#fff",
    border: 0,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
  },
};

const bodyStyle: React.CSSProperties = {
  padding: "32px",
};
const companyHeaderStyle: React.CSSProperties = {
  textAlign: "center",
  marginBottom: 32,
};
const companyNameStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 6px",
  letterSpacing: "-0.01em",
};
const subHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.12em",
  color: PALETTE.ink3,
  fontWeight: 600,
  textTransform: "uppercase",
};
const toolbarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  marginBottom: 14,
};
const toolBtnStyle: React.CSSProperties = {
  background: "#fff",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 500,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "inherit",
};
const footMetaStyle: React.CSSProperties = {
  marginTop: 16,
  fontSize: 11.5,
  color: PALETTE.ink4,
  textAlign: "right",
};